
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { debugLog, debugError } from '@/config/debug';

const AuthGoogleCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { updateUser } = useAuth();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isFirstUseModalOpen, setIsFirstUseModalOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const hasExecuted = useRef(false);
  const isNavigating = useRef(false);

  useEffect(() => {
    // Prevent multiple executions
    if (hasExecuted.current || isNavigating.current) {
      debugLog('HOOK', '🚫 GOOGLE CALLBACK: Ejecución bloqueada - hasExecuted:', hasExecuted.current, 'isNavigating:', isNavigating.current);
      return;
    }

    const handleCallback = async () => {
      hasExecuted.current = true;
      debugLog('HOOK', '🔄 GOOGLE CALLBACK: ======= INICIANDO PROCESO DE AUTENTICACIÓN =======');
      
      const urlParams = new URLSearchParams(location.search);
      const id_token = urlParams.get('id_token');
      const access_token = urlParams.get('access_token');

      debugLog('HOOK', '🔄 GOOGLE CALLBACK: URL actual:', window.location.href);
      debugLog('HOOK', '🔄 GOOGLE CALLBACK: Tokens encontrados - id_token:', !!id_token, 'access_token:', !!access_token);

      if (id_token && access_token) {
        try {
          const externalUrl = `${API_BASE_URL}/auth/google/callback?access_token=${encodeURIComponent(access_token)}`;
          debugLog('HOOK', '🔄 GOOGLE CALLBACK: URL de autenticación:', externalUrl);

          let authResponse;
          try {
            debugLog('HOOK', '🔄 GOOGLE CALLBACK: Enviando request de autenticación...');
            const response = await fetch(externalUrl, { method: 'GET' });
            authResponse = await response.json();
            debugLog('HOOK', '🔄 GOOGLE CALLBACK: Respuesta recibida:', authResponse);

            if (!response.ok) {
              throw new Error(authResponse.error?.message || 'Error desconocido');
            }
          } catch (error: any) {
            debugError('HOOK', '❌ GOOGLE CALLBACK: Error al hacer la redirección externa:', error);

            if (error.message === 'Email is already taken.') {
              debugLog('HOOK', '🚨 GOOGLE CALLBACK: Email ya existe');
              setErrorMessage(t('email_already_associated'));
              setIsErrorModalOpen(true);
              setIsLoading(false);
              return;
            }

            debugLog('HOOK', '🚨 GOOGLE CALLBACK: Error de autenticación general');
            setErrorMessage(t('error_authenticating'));
            setIsErrorModalOpen(true);
            setIsLoading(false);
            return;
          }

          debugLog('HOOK', '✅ GOOGLE CALLBACK: Autenticación exitosa');
          debugLog('HOOK', '💾 GOOGLE CALLBACK: Guardando token y usuario...');
          
          localStorage.setItem('token', authResponse.jwt);
          localStorage.setItem('mediscribe_user', JSON.stringify(authResponse.user));
          axios.defaults.headers.common['Authorization'] = `Bearer ${authResponse.jwt}`;

          debugLog('HOOK', '💾 GOOGLE CALLBACK: Datos guardados en localStorage');
          debugLog('HOOK', '🔍 GOOGLE CALLBACK: Usuario recibido:', authResponse.user);
          debugLog('HOOK', '🔍 GOOGLE CALLBACK: first_use inicial:', authResponse.user.first_use);

          // Wait a moment to ensure auth state is updated
          debugLog('HOOK', '⏱️ GOOGLE CALLBACK: Esperando actualización de estado...');
          await new Promise(resolve => setTimeout(resolve, 200));

          // Fetch user data from /users/me to get the most up-to-date information
          try {
            debugLog('HOOK', '🔄 GOOGLE CALLBACK: Obteniendo datos actualizados del usuario...');
            const userResponse = await axios.get(`${API_BASE_URL}/users/me`);
            const userData = userResponse.data;
            
            debugLog('HOOK', '👤 GOOGLE CALLBACK: Datos del usuario actualizados:', userData);
            debugLog('HOOK', '🔍 GOOGLE CALLBACK: first_use actualizado:', userData.first_use);
            debugLog('HOOK', '🔍 GOOGLE CALLBACK: tipo de first_use:', typeof userData.first_use);
            
            localStorage.setItem('mediscribe_user', JSON.stringify(userData));
            updateUser(userData);

            debugLog('HOOK', '⏱️ GOOGLE CALLBACK: Esperando actualización del contexto...');
            await new Promise(resolve => setTimeout(resolve, 200));

            // Check first_use with explicit boolean comparison
            if (userData.first_use === true) {
              debugLog('HOOK', '✨ GOOGLE CALLBACK: Usuario de primer uso detectado, mostrando modal');
              setIsFirstUseModalOpen(true);
              setIsLoading(false);
            } else {
              debugLog('HOOK', '🏠 GOOGLE CALLBACK: Usuario existente, navegando al dashboard');
              debugLog('HOOK', '🚀 GOOGLE CALLBACK: Ejecutando navegación...');
              isNavigating.current = true;
              
              // Use window.location.href as primary navigation method to avoid conflicts
              debugLog('HOOK', '🔄 GOOGLE CALLBACK: Usando window.location.href para navegar');
              window.location.href = '/dashboard';
            }
          } catch (userError) {
            debugError('HOOK', '❌ GOOGLE CALLBACK: Error obteniendo datos de usuario:', userError);
            // Fallback to original user data
            debugLog('HOOK', '🔄 GOOGLE CALLBACK: Usando datos originales como fallback');
            debugLog('HOOK', '🔍 GOOGLE CALLBACK: first_use (fallback):', authResponse.user.first_use);
            
            updateUser(authResponse.user);
            
            if (authResponse.user.first_use === true) {
              debugLog('HOOK', '✨ GOOGLE CALLBACK: Usuario de primer uso (fallback), mostrando modal');
              setIsFirstUseModalOpen(true);
              setIsLoading(false);
            } else {
              debugLog('HOOK', '🏠 GOOGLE CALLBACK: Usuario existente (fallback), navegando al dashboard');
              debugLog('HOOK', '🚀 GOOGLE CALLBACK: Ejecutando navegación (fallback)...');
              isNavigating.current = true;
              window.location.href = '/dashboard';
            }
          }
        } catch (error) {
          debugError('HOOK', '❌ GOOGLE CALLBACK: Error general al manejar el callback:', error);
          setErrorMessage(t('unexpected_error'));
          setIsErrorModalOpen(true);
          setIsLoading(false);
        }
      } else {
        debugError('HOOK', '❌ GOOGLE CALLBACK: Tokens faltantes en URL');
        debugLog('HOOK', '🔍 GOOGLE CALLBACK: id_token:', !!id_token, 'access_token:', !!access_token);
        setErrorMessage(t('error_authenticating'));
        setIsErrorModalOpen(true);
        setIsLoading(false);
      }
    };

    handleCallback();
  }, []);

  const handleFirstUseSubmit = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setFormError(t('complete_all_fields'));
      return;
    }

    debugLog('HOOK', '📝 FIRST USE: ======= INICIANDO ACTUALIZACIÓN DE PRIMER USO =======');
    debugLog('HOOK', '📝 FIRST USE: Datos a enviar:', { firstName, lastName });

    try {
      const jwt = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('mediscribe_user') || '{}');

      debugLog('HOOK', '📝 FIRST USE: Usuario actual:', user);
      debugLog('HOOK', '📝 FIRST USE: JWT disponible:', !!jwt);

      const updateData = {
        first_name: firstName,
        last_name: lastName,
        name: `${firstName} ${lastName}`,
        lang: i18n.language.split('-')[0],
        first_use: false,
        id: user.id,
        recreate: true
      };

      debugLog('HOOK', '📝 FIRST USE: Datos de actualización:', updateData);

      const response = await axios.put(`${API_BASE_URL}/users/${user.id}`, updateData, {
        headers: {
          'Authorization': `Bearer ${jwt}`
        }
      });

      const updatedUser = response.data;
      debugLog('HOOK', '✅ FIRST USE: Usuario actualizado exitosamente:', updatedUser);
      debugLog('HOOK', '🔍 FIRST USE: first_use después de actualización:', updatedUser.first_use);
      
      localStorage.setItem('mediscribe_user', JSON.stringify(updatedUser));
      updateUser(updatedUser);

      debugLog('HOOK', '🎉 FIRST USE: Cerrando modal y mostrando toast...');
      setIsFirstUseModalOpen(false);
      
      toast({
        title: t('welcome_message'),
        description: t('registration_complete_success'),
      });
      
      debugLog('HOOK', '🚀 FIRST USE: Preparando navegación a onboarding...');
      await new Promise(resolve => setTimeout(resolve, 500)); // Wait for toast to show
      
      isNavigating.current = true;
      debugLog('HOOK', '🔄 FIRST USE: Navegando a /onboarding con window.location.href');
      window.location.href = '/onboarding';
      
    } catch (error) {
      debugError('HOOK', '❌ FIRST USE: Error al actualizar el usuario:', error);
      if (error.response) {
        debugError('HOOK', '❌ FIRST USE: Respuesta de error:', error.response.data);
        debugError('HOOK', '❌ FIRST USE: Status de error:', error.response.status);
      }
      setFormError(t('unexpected_error'));
    }
  };

  // Add timeout for loading state
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isLoading && !isErrorModalOpen && !isFirstUseModalOpen) {
        debugLog('HOOK', '⏰ GOOGLE CALLBACK: Timeout de autenticación alcanzado');
        setErrorMessage('La autenticación está tomando demasiado tiempo. Por favor, inténtalo de nuevo.');
        setIsErrorModalOpen(true);
        setIsLoading(false);
      }
    }, 15000); // Increased to 15 seconds

    return () => clearTimeout(timeout);
  }, [isLoading, isErrorModalOpen, isFirstUseModalOpen]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center space-y-4">
            <img src="/logo.png" alt="Clara Logo" className="w-16 h-16 mx-auto mb-4" />
            <Loader2 className="h-8 w-8 animate-spin mx-auto" />
            <p className="text-muted-foreground">Procesando autenticación...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <Dialog open={isErrorModalOpen} onOpenChange={(open) => {
        setIsErrorModalOpen(open);
        if (!open) {
          debugLog('HOOK', '🔄 ERROR MODAL: Navegando de vuelta al login');
          isNavigating.current = true;
          window.location.href = '/';
        }
      }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('error_authenticating_title')}</DialogTitle>
            <DialogDescription>{errorMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => {
              debugLog('HOOK', '🔄 ERROR MODAL: Botón presionado, navegando al login');
              isNavigating.current = true;
              window.location.href = '/';
            }}>{t('return_to_login')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isFirstUseModalOpen} onOpenChange={setIsFirstUseModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('welcome_message')}</DialogTitle>
            <DialogDescription>{t('complete_info')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">{t('name')}</Label>
              <Input 
                id="firstName" 
                placeholder={t('name')} 
                value={firstName} 
                onChange={(e) => setFirstName(e.target.value)} 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">{t('last_name')}</Label>
              <Input 
                id="lastName" 
                placeholder={t('last_name')} 
                value={lastName} 
                onChange={(e) => setLastName(e.target.value)} 
              />
            </div>
            {formError && <p className="text-destructive text-sm">{formError}</p>}
          </div>
          <DialogFooter>
            <Button onClick={handleFirstUseSubmit} className="w-full">
              {t('access')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuthGoogleCallback;
